/**
 * Return the localized name for an entity that has both name_ar and name (English).
 * Prefers the language-specific field first, then falls back to the other, then to a raw key.
 * For English mode, if `name` is empty but `name_ar` matches a well-known Arabic school-domain
 * pattern (grade / stage / class letter), we auto-translate on the fly.
 */
export function localName(item, lang = "ar") {
  if (!item) return "";
  if (lang === "ar") return item.name_ar || item.name || "";
  // Non-Arabic: prefer English `name`, but if it looks Arabic, try to translate it.
  const en = item.name;
  if (en && !isArabic(en)) return en;
  // Try to translate common patterns (from name_ar OR arabic-in-name field)
  const arabicSrc = item.name_ar || en || "";
  if (arabicSrc) {
    const translated = autoTranslateEduName(arabicSrc);
    if (translated) return translated;
  }
  return en || item.name_ar || "";
}

function isArabic(s) {
  return /[\u0600-\u06FF]/.test(s || "");
}

/**
 * For entities that use `full_name` / `full_name_ar` (students, teachers, employees, drivers).
 */
export function localFullName(item, lang = "ar") {
  if (!item) return "";
  if (lang === "ar") return item.full_name_ar || item.full_name || "";
  return item.full_name || item.full_name_ar || "";
}

/**
 * Auto-translate common Arabic school-domain names to English when no explicit `name` is stored.
 * Handles Arabic ordinal numbers, stage names, and class letters (أ/ب/ج/د).
 */
const ARABIC_ORDINALS = {
  "الأول": "1", "الاول": "1", "الأولى": "1",
  "الثاني": "2", "الثانية": "2",
  "الثالث": "3", "الثالثة": "3",
  "الرابع": "4", "الرابعة": "4",
  "الخامس": "5", "الخامسة": "5",
  "السادس": "6", "السادسة": "6",
  "السابع": "7", "السابعة": "7",
  "الثامن": "8", "الثامنة": "8",
  "التاسع": "9", "التاسعة": "9",
  "العاشر": "10", "العاشرة": "10",
  "الحادي عشر": "11", "الثاني عشر": "12",
};

const ARABIC_STAGE = {
  "الابتدائي": "Primary", "الابتدائية": "Primary",
  "المتوسط": "Middle", "المتوسطة": "Middle",
  "الثانوي": "Secondary", "الثانوية": "Secondary",
  "الروضة": "Kindergarten",
  "التمهيدي": "Pre-K",
};

const ARABIC_CLASS_LETTER = {
  "أ": "A", "ا": "A", "ب": "B", "ج": "C", "د": "D", "هـ": "E", "ه": "E", "و": "F", "ز": "G",
};

function autoTranslateEduName(nameAr) {
  if (!nameAr) return "";
  const s = nameAr.trim();
  // Pattern: "الصف الأول الابتدائي - أ"
  const gradeMatch = s.match(/^(?:الصف\s+)?([^-]+?)(?:\s+([^\s-]+))?\s*(?:[-–]\s*(.+))?$/);
  if (!gradeMatch) return "";
  const [, ord, stage, section] = gradeMatch;

  // Match ordinal
  let gradeNum = "";
  for (const [ar, num] of Object.entries(ARABIC_ORDINALS)) {
    if (ord && ord.trim().includes(ar)) { gradeNum = num; break; }
  }
  // Match stage
  let stageEn = "";
  const stageSearch = (stage || "") + " " + (ord || "");
  for (const [ar, en] of Object.entries(ARABIC_STAGE)) {
    if (stageSearch.includes(ar)) { stageEn = en; break; }
  }
  // Match section letter
  let sectionEn = "";
  if (section) {
    const secTrim = section.trim();
    sectionEn = ARABIC_CLASS_LETTER[secTrim] || secTrim;
  }
  if (gradeNum) {
    let out = `Grade ${gradeNum}`;
    if (stageEn) out += ` ${stageEn}`;
    if (sectionEn) out += ` - ${sectionEn}`;
    return out;
  }
  // Stand-alone stage names
  if (stageEn && !gradeNum) return stageEn;
  return "";
}

